import { ArrowLeftOutlined, ArrowRightOutlined, CopyOutlined, UserOutlined, YoutubeFilled } from "@ant-design/icons";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  type InputRef,
  Row,
  Segmented,
  Select,
  Steps,
  Typography,
  message,
} from "antd";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { InlineWidget } from "react-calendly";
import { useNavigate } from "react-router-dom";
import YouTube, { type YouTubePlayer } from "react-youtube";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import useUserInfo from "../../hooks/useUserInfo";
import { getOnboardingCatalog, patchUserInfo, sendTeamInvitation } from "../../service/auth.service";
import { OnboardingData, OnboardingFormData } from "../../types/index.type";
import { ehrOptions, referralSource, tutorial } from "./onboarding.constant";
import styles from "./onboarding.module.scss";

const OnboardingForm = ({
  formStep,
  setFormStep,
}: {
  formStep: number;
  setFormStep: React.Dispatch<React.SetStateAction<1 | 2 | 3 | 4 | 5 | 6>>;
}) => {
  const componentName = "onboarding";
  const youtubeVideoRef = useRef<YouTubePlayer | null>(null);
  const isMobile = useIsMobileView();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { fetchUserInfo } = useUserInfo();
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState<string[]>([]);
  const emailRef = useRef<InputRef | null>(null);
  const [step, setStep] = useState<number>(0);
  const [playingVideos, setPlayingVideos] = useState<{ [key: string]: boolean }>({});
  const [practiceAreas, setPracticeAreas] = useState<{ label: string; value: string }[]>([]);
  const [specialtiesByArea, setSpecialtiesByArea] = useState<Record<string, { label: string; value: string }[]>>({});
  const [selectedPracticeArea, setSelectedPracticeArea] = useState<string | undefined>("behavioral_health");
  const [workAs, setWorkAs] = useState<string>("solo");

  useEffect(() => {
    if (formStep === 5) {
      const interval = setInterval(() => {
        setStep((prevStep) => (prevStep < 3 ? prevStep + 1 : prevStep));
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [formStep]);

  useEffect(() => {
    if (formStep === 5 && step >= 3) {
      const timeout = setTimeout(async () => {
        // Mark onboarding as completed before navigating
        try {
          await patchUserInfo({
            onboarding_data: {
              completed: true,
            },
          });
          await fetchUserInfo();
        } catch (error) {
          console.error("Error completing onboarding:", error);
        }
        navigate("/home");
      }, 1000);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStep, step, navigate]);

  useEffect(() => {
    setHasAnimated(true);
  }, []);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInviteEmail = async () => {
    const email = emailRef.current?.input?.value;
    if (!email) return;
    if (invitedEmail.includes(email)) {
      message.error("Email already invited");
      return;
    }
    //validate email
    if (!validateEmail(email)) {
      message.error("Invalid email");
      return;
    }

    try {
      await sendTeamInvitation(email);
      setInvitedEmail([...invitedEmail, email]);
      if (emailRef.current?.input) {
        emailRef.current.input.value = "";
      }
      message.success("Invite sent successfully!");
    } catch (error) {
      console.error("Error sending team invitation:", error);
      message.error("Failed to send invitation. Please try again.");
    }
  };

  useEffect(() => {
    // Bootstrap catalogs from BE
    (async () => {
      try {
        const data = await getOnboardingCatalog();
        const pa = (data.practice_areas || []).map((p) => ({ label: p.label, value: p.code }));
        const sba: Record<string, { label: string; value: string }[]> = {};
        Object.entries(data.specialties_by_area || {}).forEach(([area, list]) => {
          sba[area] = (list as any[]).map((s: any) => ({ label: s.label, value: s.code }));
        });
        setPracticeAreas(pa);
        setSpecialtiesByArea(sba);
      } catch (e) {
        console.error("Failed to load onboarding catalogs", e);
      }
    })();
  }, []);

  const handleContinue = async (values: OnboardingFormData) => {
    setWorkAs(values.work_as);
    try {
      const onboardingData: OnboardingData = {
        specialty: values.specialty,
        referral_source: values.referral_source,
        ehr: values.ehr,
        work_as: values.work_as,
        completed: false,
        start_time: new Date().toISOString(),
      };

      await patchUserInfo({
        practice_area: values.practice_area,
        onboarding_data: onboardingData,
      });

      message.success("Profile updated successfully!");
      if (values.work_as === "solo" || userInfo.group_name) {
        setFormStep(2); // Move to next step after successful submission
      } else {
        setFormStep(6);
      }
    } catch (error) {
      console.error("Error updating onboarding data:", error);
      message.error("Failed to update profile. Please try again.");
    }
  };

  const handleVideoPlay = (videoId: string) => {
    setPlayingVideos((prev) => ({ ...prev, [videoId]: true }));
  };

  const handleVideoEnd = (videoId: string) => {
    setPlayingVideos((prev) => ({ ...prev, [videoId]: false }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const specialityTitle = useMemo(() => {
    const area = selectedPracticeArea || "behavioral_health";
    const options = specialtiesByArea[area] || [];
    const specialty = options.find((opt) => opt.value === form.getFieldValue("specialty"));
    return specialty?.label;
  }, [form.getFieldValue("specialty"), selectedPracticeArea, specialtiesByArea]);

  const renderCTA = () => {
    return (
      <>
        {formStep === 1 && (
          <Button type="primary" className={styles[`${componentName}__continue-btn`]} onClick={() => form.submit()}>
            Continue
          </Button>
        )}
        {formStep === 6 && (
          <Flex gap={16} className={styles[`${componentName}__action-buttons`]}>
            <Button onClick={() => setFormStep(3)} className={styles[`${componentName}__copy-link-btn`]}>
              Remind me later
            </Button>
            <Button type="primary" className={styles[`${componentName}__continue-btn`]} onClick={() => setFormStep(3)}>
              I've booked
            </Button>
          </Flex>
        )}
        {formStep === 2 && (
          <Flex gap={16} className={styles[`${componentName}__action-buttons`]}>
            <Button
              icon={<CopyOutlined />}
              onClick={async () => {
                await navigator.clipboard.writeText(`${window.location.origin}/register`);
                message.success("Invite link copied to clipboard");
              }}
              className={styles[`${componentName}__copy-link-btn`]}
            >
              Share Invite Link
            </Button>
            <Button type="primary" className={styles[`${componentName}__continue-btn`]} onClick={() => setFormStep(3)}>
              Continue
            </Button>
          </Flex>
        )}
        {formStep === 3 && (
          <Flex gap={16} className={styles[`${componentName}__action-buttons`]}>
            <Button className={styles[`${componentName}__copy-link-btn`]} onClick={() => setFormStep(4)}>
              Remind me later
            </Button>
            <Button
              type="primary"
              className={styles[`${componentName}__continue-btn`]}
              onClick={() => {
                window.open(
                  "https://chromewebstore.google.com/detail/nudge-ai/khnjmojbacjmfbplefmcinlmlbifhjnj",
                  "_blank",
                );
                setFormStep(4);
              }}
            >
              Install Chrome Extension
            </Button>
          </Flex>
        )}
        {formStep === 4 && (
          <Flex gap={16} justify="center" className={styles[`${componentName}__action-buttons-full`]}>
            <Button
              icon={<YoutubeFilled style={{ color: "#FF0000" }} />}
              onClick={() =>
                window.open(
                  "https://youtube.com/playlist?list=PL9lb9UfUiaJxz04wfQuQ0b8Khxln5HN-r&si=L9yxE9SVMHTlbTO6",
                  "_blank",
                )
              }
            >
              View Entire Tutorial Playlist
              <ArrowRightOutlined />
            </Button>
            <Button type="primary" onClick={() => setFormStep(5)}>
              Get Started
              <ArrowRightOutlined />
            </Button>
          </Flex>
        )}
      </>
    );
  };

  const previousClicked = () => {
    if (formStep === 6) {
      setFormStep(1);
    } else if (formStep === 3 && workAs !== "solo" && !userInfo.group_name) {
      setFormStep(6);
    } else {
      setFormStep((pre) => (pre - 1) as 1 | 2 | 3 | 4 | 5);
    }
  };

  return (
    <Flex
      vertical
      className={`${styles[`${componentName}__form-wrapper`]} ${hasAnimated ? styles[`${componentName}__form-wrapper--animated`] : ""}`}
      gap={16}
    >
      <Flex vertical align="center" gap={4} className={styles[`${componentName}__section-wrapper`]}>
        <Typography.Text
          className={
            formStep === 5
              ? styles[`${componentName}__section-title-custom`]
              : styles[`${componentName}__section-title`]
          }
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <title>Customise your journey</title>
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.50001 2.75C6.63581 2.75003 6.76791 2.79429 6.87632 2.87608C6.98472 2.95788 7.06354 3.07275 7.10084 3.20333L7.77834 5.575C7.92422 6.08577 8.19788 6.55092 8.57349 6.92652C8.94909 7.30213 9.41424 7.57579 9.92501 7.72167L12.2967 8.39917C12.4272 8.43655 12.5419 8.5154 12.6237 8.6238C12.7054 8.7322 12.7496 8.86426 12.7496 9C12.7496 9.13575 12.7054 9.2678 12.6237 9.3762C12.5419 9.4846 12.4272 9.56345 12.2967 9.60083L9.92501 10.2783C9.41424 10.4242 8.94909 10.6979 8.57349 11.0735C8.19788 11.4491 7.92422 11.9142 7.77834 12.425L7.10084 14.7967C7.06346 14.9272 6.98461 15.0419 6.87621 15.1236C6.76781 15.2054 6.63575 15.2496 6.50001 15.2496C6.36426 15.2496 6.23221 15.2054 6.12381 15.1236C6.01541 15.0419 5.93656 14.9272 5.89917 14.7967L5.22167 12.425C5.0758 11.9142 4.80214 11.4491 4.42653 11.0735C4.05092 10.6979 3.58577 10.4242 3.07501 10.2783L0.703341 9.60083C0.572845 9.56345 0.458067 9.4846 0.37636 9.3762C0.294652 9.2678 0.250458 9.13575 0.250458 9C0.250458 8.86426 0.294652 8.7322 0.37636 8.6238C0.458067 8.5154 0.572845 8.43655 0.703341 8.39917L3.07501 7.72167C3.58577 7.57579 4.05092 7.30213 4.42653 6.92652C4.80214 6.55092 5.0758 6.08577 5.22167 5.575L5.89917 3.20333C5.93647 3.07275 6.01529 2.95788 6.1237 2.87608C6.23211 2.79429 6.3642 2.75003 6.50001 2.75ZM14 0.25C14.1394 0.249922 14.2749 0.29647 14.3848 0.382239C14.4947 0.468007 14.5728 0.588071 14.6067 0.723334L14.8217 1.58667C15.0183 2.37 15.63 2.98167 16.4133 3.17833L17.2767 3.39333C17.4122 3.4269 17.5326 3.50488 17.6186 3.61483C17.7047 3.72479 17.7514 3.86038 17.7514 4C17.7514 4.13962 17.7047 4.27521 17.6186 4.38517C17.5326 4.49512 17.4122 4.5731 17.2767 4.60667L16.4133 4.82167C15.63 5.01833 15.0183 5.63 14.8217 6.41333L14.6067 7.27667C14.5731 7.41219 14.4951 7.53257 14.3852 7.61861C14.2752 7.70465 14.1396 7.7514 14 7.7514C13.8604 7.7514 13.7248 7.70465 13.6148 7.61861C13.5049 7.53257 13.4269 7.41219 13.3933 7.27667L13.1783 6.41333C13.0822 6.02869 12.8833 5.67742 12.6029 5.39706C12.3226 5.11671 11.9713 4.91782 11.5867 4.82167L10.7233 4.60667C10.5878 4.5731 10.4674 4.49512 10.3814 4.38517C10.2954 4.27521 10.2486 4.13962 10.2486 4C10.2486 3.86038 10.2954 3.72479 10.3814 3.61483C10.4674 3.50488 10.5878 3.4269 10.7233 3.39333L11.5867 3.17833C11.9713 3.08218 12.3226 2.88329 12.6029 2.60294C12.8833 2.32258 13.0822 1.97131 13.1783 1.58667L13.3933 0.723334C13.4272 0.588071 13.5053 0.468007 13.6152 0.382239C13.7251 0.29647 13.8606 0.249922 14 0.25ZM12.75 11.5C12.8813 11.4999 13.0092 11.5412 13.1157 11.6179C13.2222 11.6946 13.3019 11.803 13.3433 11.9275L13.6717 12.9133C13.7967 13.2858 14.0883 13.5792 14.4617 13.7033L15.4475 14.0325C15.5716 14.0742 15.6796 14.1538 15.756 14.2601C15.8325 14.3664 15.8736 14.4941 15.8736 14.625C15.8736 14.7559 15.8325 14.8836 15.756 14.9899C15.6796 15.0962 15.5716 15.1758 15.4475 15.2175L14.4617 15.5467C14.0892 15.6717 13.7958 15.9633 13.6717 16.3367L13.3425 17.3225C13.3008 17.4466 13.2212 17.5546 13.1149 17.631C13.0086 17.7075 12.881 17.7486 12.75 17.7486C12.6191 17.7486 12.4914 17.7075 12.3851 17.631C12.2788 17.5546 12.1992 17.4466 12.1575 17.3225L11.8283 16.3367C11.767 16.1527 11.6636 15.9856 11.5265 15.8485C11.3894 15.7114 11.2223 15.6081 11.0383 15.5467L10.0525 15.2175C9.92837 15.1758 9.82046 15.0962 9.74401 14.9899C9.66756 14.8836 9.62643 14.7559 9.62643 14.625C9.62643 14.4941 9.66756 14.3664 9.74401 14.2601C9.82046 14.1538 9.92837 14.0742 10.0525 14.0325L11.0383 13.7033C11.4108 13.5783 11.7042 13.2867 11.8283 12.9133L12.1575 11.9275C12.1989 11.8031 12.2785 11.6949 12.3848 11.6182C12.4911 11.5414 12.6189 11.5001 12.75 11.5Z"
              fill="url(#paint0_linear_4709_748)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_4709_748"
                x1="9.00093"
                y1="0.25"
                x2="9.00093"
                y2="17.7486"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="#6366F1" />
                <stop offset="1" stop-color="#9A9CFF" />
              </linearGradient>
            </defs>
          </svg>
          {formStep === 1 && <span>Personalize for your practice</span>}
          {formStep === 2 && <span>Personalize for your practice</span>}
          {formStep === 3 && <span>Personalize for your practice</span>}
          {formStep === 4 && <span>Learn how to fully leverage Nudge</span>}
          {formStep === 6 && <span>Book a Demo</span>}
        </Typography.Text>
        <Typography.Title level={3} className={styles[`${componentName}__title`]}>
          {formStep === 1 && <span>Get Started with Nudge</span>}
          {formStep === 2 && <span>Add Your Colleagues</span>}
          {formStep === 3 && <span>Get instant EHR integration</span>}
          {formStep === 4 && <span>Master Nudge in 7 Minutes</span>}
          {formStep === 5 && <span>Customizing Your Experience</span>}
          {formStep === 6 && <span>Schedule a Demo to Discover How Nudge Works for Group Practices</span>}
        </Typography.Title>
        <Typography.Text className={styles[`${componentName}__description`]}>
          {formStep === 1 && <span>Unlock AI-powered Documentation, Coding and Analytics</span>}
          {formStep === 2 && <span>Give your colleagues 8 hours back every week</span>}
          {formStep === 4 && <span>Watch 3 quick tutorials to get started</span>}
        </Typography.Text>
        {formStep === 3 && (
          <Flex gap={16} className={styles[`${componentName}__description-badges-cont`]}>
            <div className={styles[`${componentName}__description-badge`]}>One-Click Note Transfer</div>
            <div className={styles[`${componentName}__description-badge`]}>Compatible with 20+ EHRs</div>
            <div className={styles[`${componentName}__description-badge`]}>Minimal Setup</div>
          </Flex>
        )}
      </Flex>

      {(formStep === 1 || formStep === 2) && (
        <Card className={`${styles[`${componentName}__card`]} step_${formStep}`}>
          {formStep === 1 && (
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                work_as: "solo",
              }}
              onFinish={handleContinue}
            >
              <Form.Item
                name="practice_area"
                label="My practice area is"
                rules={[{ required: true, message: "Please select your practice area" }]}
              >
                <Select
                  onChange={(v) => {
                    setSelectedPracticeArea(v);
                    // Reset specialty when practice area changes
                    form.setFieldsValue({ specialty: undefined });
                  }}
                  placeholder="Select your practice area"
                >
                  {practiceAreas.map((opt: { label: string; value: string }) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="specialty"
                label="My specialty is"
                rules={[{ required: true, message: "Please select your specialty" }]}
              >
                <Select placeholder="Select your specialty" disabled={!selectedPracticeArea}>
                  {(specialtiesByArea[selectedPracticeArea || "behavioral_health"] || []).map(
                    (opt: { label: string; value: string }) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ),
                  )}
                </Select>
              </Form.Item>

              <Row gutter={16}>
                <Col span={24} md={12}>
                  <Form.Item
                    name="referral_source"
                    label="How did you hear about us?"
                    rules={[{ required: true, message: "Please select the referral source" }]}
                  >
                    <Select placeholder="Select referral source">
                      {referralSource.map((opt: { label: string; value: string }) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24} md={12}>
                  <Form.Item
                    name="ehr"
                    label="What EHR do you use?"
                    rules={[{ required: true, message: "Please select your EHR system" }]}
                  >
                    <Select showSearch optionFilterProp="children" placeholder="Select your EHR system">
                      {ehrOptions.map((opt: { label: string; value: string }) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="work_as"
                label="How many providers would use this software in your practice?"
                rules={[{ required: true, message: "Please select any one option" }]}
                className={styles[`${componentName}__form-item`]}
              >
                <Segmented
                  block
                  options={[
                    { label: "Solo", value: "solo" },
                    { label: "2-10", value: "2-10" },
                    { label: "11-50", value: "11-50" },
                    { label: "50+", value: "50+" },
                  ]}
                />
              </Form.Item>
            </Form>
          )}
          {formStep === 2 && (
            <div className={styles[`${componentName}__team-invite`]}>
              <Form layout="vertical">
                <Form.Item label="Invite Colleagues (Optional)">
                  <Flex gap={8}>
                    <Input
                      ref={emailRef}
                      placeholder="susan@example.com"
                      className={styles[`${componentName}__invite-input`]}
                    />
                    <Button
                      type="primary"
                      className={styles[`${componentName}__send-invite-btn`]}
                      onClick={() => handleInviteEmail()}
                    >
                      Send Invite
                    </Button>
                  </Flex>
                </Form.Item>

                {invitedEmail && invitedEmail.length > 0 && (
                  <div className={styles[`${componentName}__invited-member`]}>
                    {invitedEmail.map((email: string) => (
                      <div
                        key={email}
                        style={{
                          marginBottom: email === invitedEmail[invitedEmail.length - 1] ? 0 : 16,
                        }}
                      >
                        <Flex align="center" justify="space-between">
                          <Flex align="center" gap={8}>
                            <Avatar size="small" icon={<UserOutlined />} />
                            <span>{email}</span>
                          </Flex>
                          <span className={styles[`${componentName}__invite-status`]}>Invite Sent</span>
                        </Flex>
                      </div>
                    ))}
                  </div>
                )}
              </Form>
            </div>
          )}
        </Card>
      )}
      {formStep === 6 && (
        <InlineWidget
          url="https://calendly.com/nudge-therapy/session-with-nudge-ai-demo-onboarding"
          styles={{
            width: "80vw",
          }}
        />
      )}
      {formStep === 3 && (
        <YouTube
          ref={youtubeVideoRef}
          videoId="kURMgLzcQl4"
          style={{
            width: isMobile ? "100%" : "650px",
            height: "400px",
            borderRadius: "10px",
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            border: "1px solid oklch(87.2% 0.01 258.338)",
            overflow: "hidden",
          }}
          opts={{
            width: "100%",
            height: "100%",
            playerVars: {
              autoplay: 0,
            },
          }}
        />
      )}
      {formStep === 4 && (
        <Flex
          gap={16}
          style={{
            width: "100%",
          }}
        >
          <Row
            gutter={[16, 16]}
            style={{
              width: "100%",
            }}
          >
            {tutorial.map((item) => (
              <Col span={24} md={8} key={item.id}>
                <Card className={styles[`${componentName}__tutorial-col`]}>
                  <div
                    style={{
                      width: "100%",
                      maxWidth: isMobile ? "100%" : "640px",
                      position: "relative",
                      paddingBottom: isMobile ? "75%" : "56.25%", // Increased height ratio for mobile
                      height: 0,
                      overflow: "hidden",
                      cursor: "pointer",
                    }}
                    onClick={() => handleVideoPlay(item.youtubeId)}
                  >
                    {!playingVideos[item.youtubeId] ? (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            borderRadius: "5px",
                            overflow: "hidden",
                            backgroundImage: `url(/assets/${item.thumbnail})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                          }}
                        >
                          <div
                            style={{
                              width: "80px",
                              height: "80px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <YoutubeFilled style={{ color: "#FF0000", fontSize: 48 }} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <YouTube
                        ref={youtubeVideoRef}
                        videoId={item.youtubeId}
                        opts={{
                          width: "100%",
                          height: "100%",
                          playerVars: {
                            autoplay: 1,
                          },
                        }}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                        onEnd={() => handleVideoEnd(item.youtubeId)}
                      />
                    )}
                  </div>
                  <div className={styles[`${componentName}__tile-description`]}>{item.title}</div>
                </Card>
              </Col>
            ))}
          </Row>
        </Flex>
      )}
      {formStep === 5 && (
        <Flex vertical gap={16} justify="center" align="center">
          <Steps
            progressDot
            direction="vertical"
            current={step}
            items={[
              {
                title: `Loaded ${specialityTitle} templates`,
              },
              {
                title: `Configured ${specialityTitle} coding`,
              },
              {
                title: "Personalized platform settings",
              },
            ]}
          />
        </Flex>
      )}
      <div className={styles[`${componentName}__cta-container`]}>{renderCTA()}</div>
      {formStep !== 5 && <div className={styles[`${componentName}__cta-container-mobile`]}>{renderCTA()}</div>}
      {formStep !== 1 && formStep !== 5 && (
        <Flex gap={16} justify="center" className={styles[`${componentName}__action-buttons-full`]}>
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            className={styles[`${componentName}__back-btn`]}
            onClick={previousClicked}
          >
            Back to previous step
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

const OnboardingPage = () => {
  const componentName = "onboarding";
  const [formStep, setFormStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);

  return (
    <div
      className={`${styles[`${componentName}__container`]}`}
      style={{
        overflow: formStep === 5 ? "hidden" : "auto",
      }}
    >
      <Flex vertical align="center" justify="center" className={styles[`${componentName}__content`]}>
        <OnboardingForm formStep={formStep} setFormStep={setFormStep} />
      </Flex>
      {formStep === 2 && (
        <Alert
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 1000,
          }}
          onClick={() => {
            window.open("https://calendly.com/nudge-therapy/schedule-demo-nudge-ai", "_blank");
          }}
          message={
            <span
              style={{
                color: "#B45309",
                fontWeight: 500,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              <span className={styles[`${componentName}__alert-text`]}>
                Managing a large practice? Book a session for volume discounts <ArrowRightOutlined />
              </span>
              <span className={styles[`${componentName}__alert-text-mobile`]}>
                Managing a large practice? <br /> Book a session for volume discounts <ArrowRightOutlined />
              </span>
            </span>
          }
          type="warning"
        />
      )}
      {formStep === 5 && (
        <svg
          style={{
            position: "absolute",
            bottom: -110,
            left: 0,
            width: "100%",
          }}
          width="685"
          height="265"
          viewBox="0 0 685 265"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Onboarding Step 5</title>
          <g opacity="0.2" filter="url(#filter0_f_4868_1138)">
            <ellipse cx="342.5" cy="132.5" rx="262.5" ry="52.5" fill="#6366F1" />
          </g>
          <defs>
            <filter
              id="filter0_f_4868_1138"
              x="0"
              y="0"
              width="685"
              height="265"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="40" result="effect1_foregroundBlur_4868_1138" />
            </filter>
          </defs>
        </svg>
      )}
    </div>
  );
};

export default OnboardingPage;
